import { Phone, User } from 'lucide-react';
import { type ReactNode } from 'react';

import type { GetPatientById, ListB2CUsers } from '@virtual-care/client';
import {
  displayName,
  formatBirthDate,
  formatPatientMid,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Separator,
  Spinner,
} from '@virtual-care/shadcn';

type PatientData = ListB2CUsers['records'][number] | GetPatientById;

interface ViewPatientDialogTriggerProps {
  patient: PatientData;
  trigger: ReactNode;
  open?: never;
  onOpenChange?: never;
  isLoading?: never;
}

interface ViewPatientDialogControlledProps {
  patient?: PatientData;
  trigger?: never;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
}

type ViewPatientDialogProps =
  | ViewPatientDialogTriggerProps
  | ViewPatientDialogControlledProps;

export const ViewPatientDialog = ({
  patient,
  trigger,
  open,
  onOpenChange,
  isLoading,
}: ViewPatientDialogProps) => {
  const isControlled = open !== undefined;
  const showLoading = isLoading || !patient;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!isControlled && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-2xl">
        {showLoading ? (
          <ViewPatientDialogSkeleton onClose={() => onOpenChange?.(false)} />
        ) : (
          <ViewPatientDialogContent
            patient={patient}
            isControlled={isControlled}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

function ViewPatientDialogSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Patient Details</DialogTitle>
        <DialogDescription>Loading patient information...</DialogDescription>
      </DialogHeader>
      <div className="flex items-center justify-center py-12">
        <Spinner className="size-8" />
      </div>
      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
}

function ViewPatientDialogContent({
  patient,
  isControlled,
  onOpenChange,
}: {
  patient: PatientData;
  isControlled: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const hasProfile = patient.profileCompleted;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-full">
            <User className="h-6 w-6" />
          </div>
          <div>
            <DialogTitle className="flex items-center gap-x-2">
              <p className="text-xl">
                {hasProfile ? displayName(patient) : 'Patient Details'}
              </p>
              <Badge variant={hasProfile ? 'default' : 'secondary'}>
                {hasProfile ? 'Profile Complete' : 'Profile Incomplete'}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              MID: {formatPatientMid(patient.mid)} | ID: {patient.id}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="space-y-6">
        <div>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Phone className="h-4 w-4" />
            Contact Information
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">
                Phone Number:
              </span>
              <span className="font-mono text-sm">{patient.phoneNumber}</span>
            </div>
            {hasProfile && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">Email:</span>
                <span className="text-sm">{patient.email}</span>
              </div>
            )}
          </div>
        </div>

        {hasProfile && (
          <>
            <Separator />

            {/* Personal Information */}
            <div>
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <User className="h-4 w-4" />
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div>
                    <span className="text-muted-foreground text-sm">
                      First Name:
                    </span>
                    <p className="text-sm font-medium">
                      {patient.b2cProfile?.firstName || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">
                      Last Name:
                    </span>
                    <p className="text-sm font-medium">
                      {patient.b2cProfile?.lastName || 'Not provided'}
                    </p>
                  </div>
                  {patient.b2cProfile?.parentName && (
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Parent Name:
                      </span>
                      <p className="text-sm font-medium">
                        {patient.b2cProfile.parentName}
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-muted-foreground text-sm">
                      Gender:
                    </span>
                    <p className="text-sm font-medium">
                      {patient.b2cProfile?.gender === 'male'
                        ? 'Male'
                        : patient.b2cProfile?.gender === 'female'
                          ? 'Female'
                          : 'Not provided'}
                    </p>
                  </div>
                  {patient.b2cProfile?.birthDate && (
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Birth Date:
                      </span>
                      <p className="text-sm font-medium">
                        {patient.b2cProfile.birthDate
                          ? formatBirthDate(patient.b2cProfile.birthDate)
                          : 'Not provided'}
                      </p>
                    </div>
                  )}
                  {patient.b2cProfile?.documentId && (
                    <div>
                      <span className="text-muted-foreground text-sm">
                        Document Number:
                      </span>
                      <p className="font-mono text-sm font-medium">
                        {patient.b2cProfile.documentId}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {patient.b2cProfile?.residency && (
                <div className="mt-3">
                  <span className="text-muted-foreground text-sm">
                    Residency:
                  </span>
                  <p className="text-sm font-medium">
                    {patient.b2cProfile.residency}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end">
        {isControlled ? (
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Close
          </Button>
        ) : (
          <DialogTrigger asChild>
            <Button variant="outline">Close</Button>
          </DialogTrigger>
        )}
      </div>
    </>
  );
}
